"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import { useFormik } from "formik";
import { ModuleSettingsStore } from "../../../../utils/contentstack/moduleSettingsStore.js";
import { getWarehouseOptionsDetails, getFiltersInitial, validateForm } from "../../../../utils/warehouses/findAWarehouse.lib.js";
import { TITLE_TEXT_KEY, SEARCH_FIELD_LABEL_KEY, POPUP_FILTER_LABEL_KEY, POPUP_FIND_BUTTON_LABEL_KEY, AUTOCOMPLETE_ATTRIBUTE_KEY } from "../../../../utils/warehouses/warehouseDrawer.constants.js";
import { useId, useMemo } from "react";
import { useTracking } from "react-tracking";
import { Text, TextField, Button, List, ListItem } from "@nxt-costco-com/forge-components";
import { ColorBlue500, SpaceLg } from "@nxt-costco-com/forge-design-tokens";
import { Search } from "@nxt-costco-com/forge-icons/icons/Search";
import { WarehouseOptionCheckbox } from "../../../Locations/WarehouseOptionCheckbox/index.js";
import { DATA_TEST_ID, TITLE_ID, INPUT_ID, LABEL_ID, COMPONENT_NAME } from "./constants.js";
import { getWarehouseLocationsUrl } from "./lib.js";
import { StyledCompactContainer, buttonIconSx, StyledDefaultContainer, CheckboxFieldset, listSx } from "./styles.js";
const searchFieldName = "searchText";
const FindAWarehouseFormUI = (props) => {
  const { trackEvent } = useTracking();
  const { variant = "standard", moduleSettings } = props;
  const id = useId();
  const msStore = useMemo(
    () => new ModuleSettingsStore({
      composable_window: moduleSettings.composable_window,
      key_value: moduleSettings.key_value
    }),
    [moduleSettings]
  );
  const title = msStore.getKeyValueItem(TITLE_TEXT_KEY);
  const inputFieldLabel = msStore.getKeyValueItem(SEARCH_FIELD_LABEL_KEY);
  const bodyTitle = msStore.getKeyValueItem(POPUP_FILTER_LABEL_KEY);
  const submitButtonLabel = msStore.getKeyValueItem(
    POPUP_FIND_BUTTON_LABEL_KEY
  );
  const autoCompleteAttribute = msStore.getKeyValueItem(
    AUTOCOMPLETE_ATTRIBUTE_KEY
  );
  const properties = moduleSettings.properties[0];
  const warehousePageBaseUrl = properties == null ? void 0 : properties.warehousePageURL;
  const CompactTextFieldId = `${INPUT_ID}-compact-${id}`;
  const CompactTextFieldLabelId = `${LABEL_ID}-compact-${id}`;
  const drawerOptionsDetails = useMemo(
    () => getWarehouseOptionsDetails(moduleSettings, true),
    [moduleSettings]
  );
  const initialValues = {
    filters: getFiltersInitial(moduleSettings),
    searchText: ""
  };
  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      trackEvent({
        identifier: "whSearchTerm",
        keyword: values.searchText,
        location: variant === "standard" ? "find a warehouse flyout" : "footer",
        trackType: "search"
      });
      const url = getWarehouseLocationsUrl(
        values,
        drawerOptionsDetails,
        warehousePageBaseUrl
      );
      window.location.href = url;
    },
    validate: (values) => validateForm(moduleSettings, values)
  });
  const handleSubmit = (e) => {
    e.preventDefault();
    formik.handleSubmit();
  };
  return /* @__PURE__ */ jsxs("form", { "data-testid": DATA_TEST_ID, onSubmit: handleSubmit, children: [
    variant === "compact" && /* @__PURE__ */ jsxs(StyledCompactContainer, { children: [
      /* @__PURE__ */ jsx(
        Text,
        {
          component: "h2",
          id: TITLE_ID,
          uniqueId: TITLE_ID,
          variant: "t4",
          children: title
        }
      ),
      /* @__PURE__ */ jsx(
        TextField,
        {
          endAdornment: /* @__PURE__ */ jsx(
            Button,
            {
              onClick: formik.handleSubmit,
              sx: buttonIconSx,
              title: submitButtonLabel,
              uniqueId: "submit",
              variant: "text-button",
              children: /* @__PURE__ */ jsx(
                Search,
                {
                  color: ColorBlue500,
                  height: 24,
                  width: 24
                }
              )
            }
          ),
          onChange: (e) => formik.setFieldValue(
            searchFieldName,
            e.target.value
          ),
          autoComplete: autoCompleteAttribute,
          id: CompactTextFieldId,
          inputAriaLabelledBy: `${CompactTextFieldLabelId} ${TITLE_ID}`,
          inputLabelId: CompactTextFieldLabelId,
          label: inputFieldLabel,
          uniqueId: `${INPUT_ID}-compact`,
          value: formik.values[searchFieldName]
        }
      )
    ] }),
    variant === "standard" && /* @__PURE__ */ jsxs(StyledDefaultContainer, { children: [
      /* @__PURE__ */ jsx(
        TextField,
        {
          onChange: (e) => formik.setFieldValue(
            searchFieldName,
            e.target.value
          ),
          id: `${INPUT_ID}-standard-${id}`,
          label: inputFieldLabel,
          sx: { marginBottom: SpaceLg },
          uniqueId: `${INPUT_ID}-standard`,
          value: formik.values[searchFieldName]
        }
      ),
      /* @__PURE__ */ jsxs(CheckboxFieldset, { children: [
        /* @__PURE__ */ jsx("legend", { children: /* @__PURE__ */ jsx(Text, { children: bodyTitle }) }),
        /* @__PURE__ */ jsx(List, { sx: listSx, variant: "SimpleList", children: drawerOptionsDetails.map((option) => {
          return /* @__PURE__ */ jsx(ListItem, { children: /* @__PURE__ */ jsx(
            WarehouseOptionCheckbox,
            {
              isChecked: formik.values.filters[option.id],
              onChange: (e) => formik.setFieldValue(
                "filters",
                {
                  ...formik.values.filters,
                  [option.id]: e.target.checked
                }
              ),
              option,
              uniqueId: `findawarehouse-${option.label}`
            },
            option.id
          ) }, option.id);
        }) })
      ] }),
      /* @__PURE__ */ jsx(
        Button,
        {
          onClick: formik.handleSubmit,
          sx: { width: "100%" },
          uniqueId: submitButtonLabel,
          variant: "primary",
          children: submitButtonLabel
        }
      )
    ] })
  ] });
};
FindAWarehouseFormUI.displayName = COMPONENT_NAME;
export {
  FindAWarehouseFormUI
};
